import React, { useState, useRef, useEffect } from 'react'
import moment from 'moment'
import ChatInput from './ChatInput'
import ChatHeader from './ChatHeader'
import { Divider, ActionSheet, Dialog } from 'antd-mobile'
import { formatMessageText } from '../../utils/constant'
import { FaCheck } from 'react-icons/fa'
import { getUserAvatar, hasAvatarImage } from '../../utils/avatarUtils'
import './styles/ChannelMessages.css'

const ThreadView = ({
  selectedThread,
  replies,
  users = [],
  userAvatars = [],
  onBack,
  threadInput,
  setThreadInput,
  onSendReply,
  onDeleteMessage,
  onEditMessage,
  onPinMessage,
  currentUserId,
  isEditing,
  editingMessageId,
  onCancelEdit,
  handleThreadInputChange,
  showThreadUserSuggestions,
  threadFilteredUsers,
  handleThreadUserSelect,
  onImageUpload,
  jumpToMessageId
}) => {
  const [longPressTimer, setLongPressTimer] = useState(null)
  const [activeMsgId, setActiveMsgId] = useState(null)
  const threadWindowBottomRef = useRef(null)
  const prevRepliesRef = useRef([])
  const messageRefs = useRef({})

  const formatMention = (text) => {
    let formattedText = text
    users.forEach(user => {
      const usernameRegex = new RegExp(`@${user.username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g')
      formattedText = formattedText.replace(usernameRegex, `<span class='mention'>@${user.name}</span>`)
    })
    return formattedText
  }

  useEffect(() => {
    const prevReplies = prevRepliesRef.current
    const lastPrevId = prevReplies?.[prevReplies.length - 1]?._id
    const lastCurrId = replies?.[replies.length - 1]?._id

    if (lastCurrId && lastCurrId !== lastPrevId) {
      threadWindowBottomRef.current?.scrollIntoView({ behavior: 'instant' })
    }

    prevRepliesRef.current = replies || []
  }, [replies])

  useEffect(() => {
    if (jumpToMessageId && messageRefs.current[jumpToMessageId]) {
      messageRefs.current[jumpToMessageId].scrollIntoView({ behavior: 'instant', block: 'center' })
      messageRefs.current[jumpToMessageId].classList.add('jump-highlight')
      setTimeout(() => {
        messageRefs.current[jumpToMessageId]?.classList.remove('jump-highlight')
      }, 1500)
    }
  }, [jumpToMessageId])

  if (!selectedThread) return null

  const startLongPress = (msg) => {
    setActiveMsgId(msg._id)
    const timer = setTimeout(() => {
      showActions(msg)
    }, 600)
    setLongPressTimer(timer)
  }

  const cancelLongPress = () => {
    if (longPressTimer) clearTimeout(longPressTimer)
    setActiveMsgId(null)
  }

  const showActions = (msg) => {
    const handler = ActionSheet.show({
      actions: [
        ...(msg.u?._id === currentUserId
          ? [
            {
              text: 'Edit Message',
              key: 'edit',
              onClick: () => {
                onEditMessage && onEditMessage(msg)
                // setThreadInput(msg.msg)
                handler.close()
              }
            },
            {
              text: 'Delete Message',
              key: 'delete',
              danger: true,
              onClick: async () => {
                handler.close()
                const result = await Dialog.confirm({
                  title: 'Delete Message',
                  content: 'Do you want to delete this message?',
                  cancelText: 'Cancel',
                  confirmText: 'Delete'
                })
                if (result) {
                  onDeleteMessage &&
                    onDeleteMessage({ messageId: msg._id, roomId: msg.rid })
                }
              }
            }
          ]
          : []),
        ...(!msg.pinned
          ? [
            {
              text: 'Pin to Conversation',
              key: 'pin',
              onClick: () => {
                onPinMessage && onPinMessage(msg, false) // only pin
                handler.close()
              }
            }
          ]
          : [])
      ],
      cancelText: 'Cancel',
      onClose: () => setActiveMsgId(null)
    })
  }

  return (
    <div>
      <ChatHeader title="Thread" onBack={onBack} />
      <div className="channel-messages-wrapper" style={{ backgroundColor: 'white' }}>
        <div className="message-container">
          <div className="message-header">
            <div className="message-avatar">
              {hasAvatarImage(selectedThread.u?.username, userAvatars) ? (
                <img
                  src={getUserAvatar(selectedThread.u?.username, userAvatars)}
                  alt={selectedThread.u?.username || 'User'}
                  className="avatar-image"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div className={`avatar-fallback ${hasAvatarImage(selectedThread.u?.username, userAvatars) ? 'hidden' : ''}`}>
                {getUserAvatar(selectedThread.u?.username, userAvatars)}
              </div>
            </div>
            <div className="message-header-content">
              <span className="message-sender">
                {selectedThread.u?.name || 'Unknown'}
              </span>
              <span className="message-timestamp">
                {moment(selectedThread.ts).format('MMM DD, YYYY h:mm A')}
              </span>
            </div>
          </div>
          <div className='message-content-wrapper'>
            <div
              className="message-content"
              dangerouslySetInnerHTML={{ __html: formatMessageText({ ...selectedThread, msg: formatMention(selectedThread.msg) }) }}
            />
            <div className="message-status">
              {selectedThread.u?._id === currentUserId && (
                <span>
                  <FaCheck style={{ color: selectedThread?.unread ? 'gray' : '#2b4b8f' }} />
                </span>
              )}
            </div>
          </div>
        </div>
        <Divider style={{ margin: '10px 0' }} />
        {(replies || []).length > 0 && (
          <>
            <h3>{`${(replies || []).length} ${replies.length === 1 ? 'reply' : 'replies'
              }`}</h3>
            <Divider style={{ margin: '10px 0' }} />
          </>
        )}
        <div className="thread-replies">
          {(replies || []).map((reply) => (
            <div
              key={reply._id}
              ref={el => (messageRefs.current[reply._id] = el)}
              className={`message-container
      ${reply.pinned ? 'pinned-message' : ''}
      ${activeMsgId === reply._id ? 'long-press-active' : ''}
      ${editingMessageId === reply._id ? 'editing-message' : ''}
      ${jumpToMessageId === reply._id ? 'editing-message' : ''}`
              }
              onMouseDown={() => startLongPress(reply)}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={() => startLongPress(reply)}
              onTouchEnd={cancelLongPress}
              onContextMenu={(e) => e.preventDefault()}
            >
              {reply.pinned && (
                <div className="pinned-label">
                  <span className="pin-icon">📌</span>
                  Pinned
                </div>
              )}
              <div className="message-header">
                <div className="message-avatar">
                  {hasAvatarImage(reply.u?.username, userAvatars) ? (
                    <img
                      src={getUserAvatar(reply.u?.username, userAvatars)}
                      alt={reply.u?.username || 'User'}
                      className="avatar-image"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <div className={`avatar-fallback ${hasAvatarImage(reply.u?.username, userAvatars) ? 'hidden' : ''}`}>
                    {getUserAvatar(reply.u?.username, userAvatars)}
                  </div>
                </div>
                <div className="message-header-content">
                  <span className="message-sender">
                    {reply.u?.name || 'Unknown'}
                  </span>
                  <span className="message-timestamp">
                    {moment(reply.ts).format('MMM DD, YYYY h:mm A')}
                  </span>
                </div>
              </div>
          <div className='message-content-wrapper'>
            <div
              className="message-content"
              dangerouslySetInnerHTML={{ __html: formatMessageText({ ...reply, msg: formatMention(reply.msg) }) }}
            />
          </div>
            </div>
          ))}
          <div ref={threadWindowBottomRef} />
        </div>
      </div>

      {showThreadUserSuggestions && (
        <div className="user-suggestion-popup">
          {threadFilteredUsers.map(user => (
            <div
              key={user._id}
              className="user-suggestion-item"
              onClick={() => handleThreadUserSelect(user)}
            >
              {user.name || user.username}
            </div>
          ))}
        </div>
      )}

      <ChatInput
        value={threadInput}
        onChange={handleThreadInputChange}
        onSend={onSendReply}
        disabled={!threadInput.trim()}
        placeholder="Reply in thread"
        isEditing={!!isEditing}
        onCancelEdit={onCancelEdit}
        onImageUpload={onImageUpload}
      />
    </div>
  )
}

export default ThreadView
